import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/politicas/RichTextEditor";
import { CustomSelect, Option } from "@/components/ui/react-select-custom";
import { supabase } from "@/lib/supabaseClient";
import { getStoredAccessToken } from "@/utils/keycloakRoles";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft, Send, Users, CheckSquare, Square, Shield, User, Building, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UserWithRole extends Option {
  role: string | null;
  generatorId: string | null;
}

interface Generator {
  generator_id: string;
  generator_name: string;
}

export default function SendMail() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [generators, setGenerators] = useState<Generator[]>([]);
  const [selectedGenerators, setSelectedGenerators] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaLink, setCtaLink] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingGenerators, setIsLoadingGenerators] = useState(true);

  useEffect(() => {
    fetchUsers();
    fetchGenerators();
  }, []);

  const fetchGenerators = async () => {
    try {
      const { data, error } = await supabase
        .from('generators')
        .select('generator_id, generator_name')
        .is('deleted_at', null)
        .order('generator_name');

      if (error) throw error;
      setGenerators(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar geradores",
        description: error.message
      });
    } finally {
      setIsLoadingGenerators(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('user_id, user_name, email, permission_role, generator_id')
        .not('email', 'is', null)
        .order('user_name');

      if (error) throw error;

      if (data) {
        const options: UserWithRole[] = data.map(user => ({
          value: user.user_id,
          label: `${user.user_name || 'Sem nome'} (${user.email})`,
          role: user.permission_role,
          generatorId: user.generator_id
        }));
        setUsers(options);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar usuários",
        description: error.message
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Filter users based on selected generators, name and email
  const filteredUsers = useMemo(() => {
    let result = users;
    
    // Filter by generators
    if (selectedGenerators.length > 0) {
      result = result.filter(user => user.generatorId && selectedGenerators.includes(user.generatorId));
    }
    
    // Filter by name
    if (nameFilter.trim()) {
      const searchTerm = nameFilter.trim().toLowerCase();
      result = result.filter(user => user.label.toLowerCase().includes(searchTerm));
    }
    
    // Filter by email
    if (emailFilter.trim()) {
      const searchTerm = emailFilter.trim().toLowerCase();
      result = result.filter(user => user.label.toLowerCase().includes(searchTerm));
    }
    
    return result;
  }, [users, selectedGenerators, nameFilter, emailFilter]);

  // Generator options for the multi-select
  const generatorOptions: Option[] = useMemo(() => {
    return generators.map(g => ({
      value: g.generator_id,
      label: g.generator_name
    }));
  }, [generators]);

  // Clear selected users when generator filter changes (to avoid invalid selections)
  useEffect(() => {
    const validUserIds = new Set(filteredUsers.map(u => u.value));
    const newSelectedUsers = selectedUsers.filter(id => validUserIds.has(id));
    if (newSelectedUsers.length !== selectedUsers.length) {
      setSelectedUsers(newSelectedUsers);
    }
  }, [filteredUsers]);

  const getUserCountByRole = (role: string) => {
    return filteredUsers.filter(u => u.role === role).length;
  };

  const handleSelectByRole = (role: string) => {
    const roleUserIds = filteredUsers.filter(u => u.role === role).map(u => u.value);
    const allRoleSelected = roleUserIds.every(id => selectedUsers.includes(id));
    
    if (allRoleSelected) {
      // Remove todos os usuários desse role
      setSelectedUsers(selectedUsers.filter(id => !roleUserIds.includes(id)));
    } else {
      // Adiciona todos os usuários desse role (mantendo os já selecionados)
      const newSelection = [...new Set([...selectedUsers, ...roleUserIds])];
      setSelectedUsers(newSelection);
    }
  };

  const isRoleFullySelected = (role: string) => {
    const roleUserIds = filteredUsers.filter(u => u.role === role).map(u => u.value);
    return roleUserIds.length > 0 && roleUserIds.every(id => selectedUsers.includes(id));
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.value));
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedUsers.length === 0) {
      toast({
        variant: "destructive",
        title: "Selecione destinatários",
        description: "Você precisa selecionar pelo menos um usuário."
      });
      return;
    }

    if (!subject.trim() || !message.trim()) {
      toast({
        variant: "destructive",
        title: "Campos obrigatórios",
        description: "Preencha o assunto e a mensagem."
      });
      return;
    }

    setLoading(true);

    try {
      const token = getStoredAccessToken();
      
      if (!token) {
        throw new Error('Você precisa estar autenticado para enviar emails');
      }

      const { data, error } = await supabase.functions.invoke('send-custom-email', {
        body: {
          userIds: selectedUsers,
          subject,
          message,
          ctaText: ctaText.trim() || undefined,
          ctaLink: ctaLink.trim() || undefined
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (error) throw error;

      const totalAttempted = data.total_attempted || data.sent_count;
      const failedCount = data.failed_count || 0;
      
      toast({
        title: failedCount > 0 ? "Emails enviados com erros" : "Emails enviados com sucesso!",
        description: `${data.sent_count} de ${totalAttempted} emails enviados com sucesso.${failedCount > 0 ? ` ${failedCount} falharam.` : ''}`,
        variant: failedCount > 0 ? "destructive" : "default"
      });

      // Reset form (optional, keeping users selected might be useful, but let's clear message)
      setSubject("");
      setMessage("");
      setCtaText("");
      setCtaLink("");
      setSelectedUsers([]);

    } catch (error: any) {
      console.error('Error sending email:', error);
      toast({
        variant: "destructive",
        title: "Erro ao enviar emails",
        description: error.message || "Ocorreu um erro ao processar sua solicitação."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="min-w-0 flex-1 mb-6">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/configuracoes')}
            className="p-1 rounded-md hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-[#10261F]" />
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-[#10261F] ">
            Envio de Email Manual
          </h1>
        </div>
        <p className="text-gray-600 mt-2  text-sm md:text-base">
          Envie comunicados oficiais para usuários internos e externos.
        </p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSend} className="space-y-6">
            
            {/* Filtros */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Filter className="h-4 w-4" /> Filtros de Destinatários
              </Label>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Filtro por Nome */}
                <div className="space-y-2">
                  <Label htmlFor="nameFilter" className="text-xs text-muted-foreground">Nome</Label>
                  <Input
                    id="nameFilter"
                    placeholder="Buscar por nome..."
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    className="h-9"
                  />
                </div>
                
                {/* Filtro por Email */}
                <div className="space-y-2">
                  <Label htmlFor="emailFilter" className="text-xs text-muted-foreground">Email</Label>
                  <Input
                    id="emailFilter"
                    placeholder="Buscar por email..."
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    className="h-9"
                  />
                </div>
                
                {/* Filtro por Gerador */}
                <div className="space-y-2">
                  <Label htmlFor="generatorFilter" className="text-xs text-muted-foreground">Gerador</Label>
                  <CustomSelect
                    options={generatorOptions}
                    value={selectedGenerators}
                    onChange={(vals) => setSelectedGenerators(vals as string[])}
                    isMulti={true}
                    isLoading={isLoadingGenerators}
                    placeholder="Todos os geradores"
                  />
                </div>
              </div>
              
              {(nameFilter || emailFilter || selectedGenerators.length > 0) && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {filteredUsers.length} usuário(s) encontrado(s)
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNameFilter("");
                      setEmailFilter("");
                      setSelectedGenerators([]);
                    }}
                    className="h-7 text-xs"
                  >
                    Limpar filtros
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="recipients" className="flex items-center gap-2">
                  <Users className="h-4 w-4" /> Destinatários
                </Label>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button 
                  type="button" 
                  variant={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0 ? "default" : "outline"}
                  size="sm" 
                  onClick={handleSelectAll}
                  className="h-8 text-xs"
                >
                  {selectedUsers.length > 0 && selectedUsers.length === filteredUsers.length ? (
                    <><CheckSquare className="mr-2 h-3 w-3" /> Todos ({filteredUsers.length})</>
                  ) : (
                    <><Square className="mr-2 h-3 w-3" /> Todos ({filteredUsers.length})</>
                  )}
                </Button>

                <Button 
                  type="button" 
                  variant={isRoleFullySelected('admin') ? "default" : "outline"}
                  size="sm" 
                  onClick={() => handleSelectByRole('admin')}
                  className="h-8 text-xs"
                >
                  {isRoleFullySelected('admin') ? (
                    <><CheckSquare className="mr-2 h-3 w-3" /></>
                  ) : (
                    <><Square className="mr-2 h-3 w-3" /></>
                  )}
                  <Shield className="mr-1 h-3 w-3" /> Admin ({getUserCountByRole('admin')})
                </Button>

                <Button 
                  type="button" 
                  variant={isRoleFullySelected('internal') ? "default" : "outline"}
                  size="sm" 
                  onClick={() => handleSelectByRole('internal')}
                  className="h-8 text-xs"
                >
                  {isRoleFullySelected('internal') ? (
                    <><CheckSquare className="mr-2 h-3 w-3" /></>
                  ) : (
                    <><Square className="mr-2 h-3 w-3" /></>
                  )}
                  <Building className="mr-1 h-3 w-3" /> Internal ({getUserCountByRole('internal')})
                </Button>

                <Button 
                  type="button" 
                  variant={isRoleFullySelected('standard') ? "default" : "outline"}
                  size="sm" 
                  onClick={() => handleSelectByRole('standard')}
                  className="h-8 text-xs"
                >
                  {isRoleFullySelected('standard') ? (
                    <><CheckSquare className="mr-2 h-3 w-3" /></>
                  ) : (
                    <><Square className="mr-2 h-3 w-3" /></>
                  )}
                  <User className="mr-1 h-3 w-3" /> Standard ({getUserCountByRole('standard')})
                </Button>
              </div>
              
              <CustomSelect
                options={filteredUsers}
                value={selectedUsers}
                onChange={(vals) => setSelectedUsers(vals as string[])}
                isMulti={true}
                isLoading={isLoadingUsers}
                placeholder="Selecione os usuários..."
              />
              <p className="text-xs text-muted-foreground text-right">
                {selectedUsers.length} usuário(s) selecionado(s)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Assunto</Label>
              <Input
                id="subject"
                placeholder="Ex: Atualização importante sobre..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Mensagem</Label>
              <RichTextEditor
                content={message}
                onChange={setMessage}
                placeholder="Digite o conteúdo do email aqui..."
              />
              <p className="text-xs text-muted-foreground">
                O email será enviado usando o layout padrão da Musa. Use a barra de ferramentas para formatar o texto.
              </p>
            </div>

            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="space-y-2">
                <Label htmlFor="ctaText" className="text-sm font-medium">
                  Botão CTA (Opcional)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Adicione um botão de chamada para ação no email.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ctaText">Texto do Botão</Label>
                <Input
                  id="ctaText"
                  placeholder="Ex: Acessar Portal, Ver Mais, Saiba Mais"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ctaLink">Link do Botão</Label>
                <Input
                  id="ctaLink"
                  type="url"
                  placeholder="Ex: https://portal.musa.com.br"
                  value={ctaLink}
                  onChange={(e) => setCtaLink(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {ctaText && !ctaLink && "⚠️ Informe o link para habilitar o botão"}
                  {!ctaText && ctaLink && "⚠️ Informe o texto do botão"}
                  {ctaText && ctaLink && "✓ Botão CTA configurado"}
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button type="submit" disabled={loading} className="w-full md:w-auto">
                {loading ? (
                  <>Enviando...</>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" /> Enviar Email
                  </>
                )}
              </Button>
            </div>

          </form>
        </CardContent>
      </Card>
    </div>
  );
}

